import React, { useState, useMemo } from 'react';
import { X, AlertTriangle, Upload, Save, MessageSquare, Check, FileText } from 'lucide-react';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ReworkEditModal({ execution, questions, reworkRequests, onClose, onSubmitted }) {
  const [saving, setSaving] = useState(false);
  const [updatedAnswers, setUpdatedAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [uploadedFiles, setUploadedFiles] = useState({});
  const [error, setError] = useState(null);

  // Get questions that need rework
  const reworkQuestions = useMemo(() => {
    const answers = execution?.answers || [];
    const reworkQids = new Set(answers.filter(a => a.requires_rework).map(a => a.question_id));
    
    // Get additional info from rework requests
    const reworkReasonMap = {};
    for (const rw of reworkRequests || []) {
      reworkReasonMap[rw.question_id] = rw.reason;
    }
    
    return questions.filter(q => reworkQids.has(q.id)).map(q => {
      const ans = answers.find(a => a.question_id === q.id);
      return {
        ...q,
        currentAnswer: ans,
        reworkReason: ans?.rework_reason || reworkReasonMap[q.id] || 'Please review and update your answer',
        requiresFileUpload: ans?.rework_requires_file_upload || false
      };
    });
  }, [execution, questions, reworkRequests]);

  const handleSelectAnswer = (questionId, optionId) => {
    setUpdatedAnswers(prev => ({
      ...prev,
      [questionId]: optionId
    }));
  };

  const handleCommentChange = (questionId, comment) => {
    setComments(prev => ({
      ...prev,
      [questionId]: comment
    }));
  };

  const handleFileUpload = async (questionId, file) => {
    const formData = new FormData();
    formData.append("file", file);
    const params = new URLSearchParams({ execution_id: execution.id, question_id: questionId });
    try {
      const res = await fetch(`${API_URL}/api/upload?${params}`, { method: 'POST', body: formData });
      if (res.ok) {
        const data = await res.json();
        setUploadedFiles(prev => ({
          ...prev,
          [questionId]: [...(prev[questionId] || []), data]
        }));
      }
    } catch (err) {
      console.error('File upload error:', err);
    }
  };

  const handleSubmit = async () => {
    // Validate all questions have been answered
    const unanswered = reworkQuestions.filter(q => !updatedAnswers[q.id] && !q.currentAnswer?.answer_option_id);
    if (unanswered.length > 0) {
      setError(`Please answer all ${unanswered.length} flagged question(s) before submitting.`);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Prepare the answers payload
      const answersPayload = reworkQuestions.map(q => ({
        question_id: q.id,
        answer_option_id: updatedAnswers[q.id] || q.currentAnswer?.answer_option_id,
        comment: comments[q.id] || '',
        uploaded_file_ids: (uploadedFiles[q.id] || []).map(f => f.id)
      }));

      const result = await api.submitReworkAnswers(execution.id, answersPayload);
      
      if (onSubmitted) {
        onSubmitted(result);
      }
      onClose();
    } catch (err) {
      setError(err.message || 'Failed to submit rework answers');
    }
    setSaving(false);
  };

  const getSelectedOption = (question) => {
    const selectedId = updatedAnswers[question.id] || question.currentAnswer?.answer_option_id;
    return question.answer_options?.find(o => o.id === selectedId);
  };

  const isAnswerUpdated = (questionId) => {
    return updatedAnswers[questionId] !== undefined;
  };

  if (reworkQuestions.length === 0) {
    return (
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50" onClick={onClose}>
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4" onClick={e => e.stopPropagation()}>
          <div className="text-center">
            <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">No Rework Required</h2>
            <p className="text-gray-500 mb-4">All questions have been addressed.</p>
            <button onClick={onClose} className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2 rounded-lg font-medium">
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-start justify-center z-50 overflow-auto py-8" onClick={onClose}>
      <div className="w-full max-w-3xl bg-white rounded-2xl shadow-2xl mx-4 overflow-hidden" onClick={e => e.stopPropagation()} data-testid="rework-edit-modal">
        
        {/* Header */}
        <div className="bg-gradient-to-br from-orange-500 to-amber-600 text-white px-8 py-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-xl font-bold mb-1 flex items-center gap-2">
                <AlertTriangle className="w-6 h-6" />
                Rework Required
              </h2>
              <p className="text-white/80 text-sm">
                {reworkQuestions.length} question{reworkQuestions.length > 1 ? 's' : ''} need{reworkQuestions.length === 1 ? 's' : ''} your attention
              </p>
            </div>
            <button onClick={onClose} className="text-white/70 hover:text-white">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="p-6 max-h-[70vh] overflow-auto space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
              {error}
            </div>
          )}

          {/* Rework Questions */}
          {reworkQuestions.map((q, idx) => {
            const selectedOpt = getSelectedOption(q);
            const hasUpdated = isAnswerUpdated(q.id);
            const qFiles = uploadedFiles[q.id] || [];
            
            return (
              <div key={q.id} className={`border rounded-xl p-5 transition-colors ${hasUpdated ? 'border-emerald-300 bg-emerald-50/30' : 'border-orange-200 bg-orange-50/30'}`}
                data-testid={`rework-question-${q.id}`}>
                
                {/* Question Header */}
                <div className="flex items-start gap-3 mb-4">
                  <span className="text-xs font-bold text-orange-600 bg-orange-100 rounded px-2 py-1 mt-0.5">
                    {idx + 1}
                  </span>
                  <div className="flex-1">
                    <p className="text-gray-900 font-medium leading-relaxed">{q.question_text}</p>
                    {q.explanation && <p className="text-gray-500 text-xs mt-1">{q.explanation}</p>}
                  </div>
                  {hasUpdated && (
                    <span className="text-xs font-medium text-emerald-600 bg-emerald-100 px-2 py-1 rounded">
                      Updated
                    </span>
                  )}
                </div>

                {/* Rework Reason */}
                <div className="bg-orange-100 border border-orange-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-orange-800 flex items-start gap-2">
                    <MessageSquare className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span><strong>Reviewer feedback:</strong> {q.reworkReason}</span>
                  </p>
                </div>

                {/* Answer Options */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {(q.answer_options || []).map(opt => {
                    const isSelected = (updatedAnswers[q.id] || q.currentAnswer?.answer_option_id) === opt.id;
                    return (
                      <button
                        key={opt.id}
                        onClick={() => handleSelectAnswer(q.id, opt.id)}
                        data-testid={`rework-answer-${opt.id}`}
                        className={`px-4 py-2 rounded-lg text-sm font-medium transition-all border ${
                          isSelected
                            ? opt.is_na
                              ? 'bg-gray-600 border-gray-500 text-white'
                              : 'bg-emerald-600 border-emerald-500 text-white'
                            : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300'
                        }`}
                      >
                        {opt.name}
                        {!opt.is_na && <span className="text-xs ml-1.5 opacity-60">({opt.points}pts)</span>}
                      </button>
                    );
                  })}
                </div>

                {/* Comment Field */}
                <div className="mb-3">
                  <label className="text-xs text-gray-500 mb-1 block">Your comment (optional)</label>
                  <textarea
                    value={comments[q.id] || ''}
                    onChange={e => handleCommentChange(q.id, e.target.value)}
                    placeholder="Add a note or explanation for your updated answer..."
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-900 placeholder-gray-400 outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
                    rows={2}
                    data-testid={`rework-comment-${q.id}`}
                  />
                </div>

                {/* File Upload */}
                {q.requiresFileUpload && (
                  <div>
                    <label className="flex items-center gap-2 text-sm text-blue-700 bg-blue-50 px-3 py-2 rounded-lg cursor-pointer hover:bg-blue-100 transition-colors w-fit">
                      <Upload className="w-4 h-4" />
                      <span>Upload Supporting Document</span>
                      <input
                        type="file"
                        className="hidden"
                        onChange={e => {
                          if (e.target.files?.[0]) handleFileUpload(q.id, e.target.files[0]);
                        }}
                      />
                    </label>
                    {qFiles.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-2">
                        {qFiles.map(f => (
                          <div key={f.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-2.5 py-1.5 text-xs">
                            <FileText className="w-3 h-3 text-gray-400" />
                            <span className="text-gray-700">{f.filename}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {/* Submit Button */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
            <button
              onClick={onClose}
              className="px-5 py-2.5 text-sm font-medium text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={saving}
              data-testid="submit-rework-btn"
              className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white px-6 py-2.5 rounded-xl font-semibold transition-colors"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Submitting...' : 'Submit Updates'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
